import React, {useMemo, useState} from "react";
import {DropdownGroup, Table} from "xps-react";
import {Dropdown, DropdownItem} from "../../components";
import {Link, useHistory} from "react-router-dom";
import {PartnerDashboardProfile} from "../models/ProfileResponse";
import {toDisplayDateFormat} from "../../utils/dateUtils";
import {
    NATIONAL_SAMPLES_DEFAULT_PAGE_SIZE,
    PARTNER_DASHBOARD_MIN_ROWS,
    PARTNER_DASHBOARD_PAGE_SIZES,
    sortClientMapping,
    sortProfiles,
    SortType
} from "./partnerDashboardConstants";
import HeaderIcon from "./HeaderIcon";
import {ProfileProposalActionMenu} from "../ClientProfile/ProfileDetails/ProfileProposalActionMenu";
import LoadingIndicator from "../../pages/LoadingIndicator";

export type NationalSampleFilterOption = 'activeClients' | 'archivedClients';

export interface INationalSamplesSection {
    profiles: PartnerDashboardProfile[];
    isLoading: boolean;
    isDuplicatingNationalSample: boolean;
    filterOption: NationalSampleFilterOption;
    isNationalSampleAdmin: boolean;
    onDuplicateNationalSample: (profileId: string) => void;
    onArchiveNationalSample: (profileId: string) => void;
    onRestoreNationalSample: (profileId: string) => void;
    searchQuery: string; // New prop for the search query
    isSearchActive: boolean; // New prop for whether a search is active
}

const NATIONAL_SAMPLE_FILTERS: Record<NationalSampleFilterOption, (profile: PartnerDashboardProfile) => boolean> = {
    activeClients: profile => !profile.archived && profile.activeClient,
    archivedClients: profile => !profile.activeClient && !profile.archived,
};

const NO_NATIONAL_SAMPLES_TEXT = 'You have no national samples available.';
const NO_SEARCHED_NATIONAL_SAMPLES_TEXT = 'No matching national samples could be found.';
const NATIONAL_SAMPLE_EMPTY_TEXT: Record<NationalSampleFilterOption, string> = {
    activeClients: NO_SEARCHED_NATIONAL_SAMPLES_TEXT,
    archivedClients: 'No national samples have been archived.',
};

const getNoDataText = (
    hasNoNationalSamples: boolean,
    isLoading: boolean,
    filterOption: NationalSampleFilterOption
): string | null => {
    if (isLoading || hasNoNationalSamples) return null;

    return NATIONAL_SAMPLE_EMPTY_TEXT[filterOption];
};

const isActiveNationalSample = (profile: PartnerDashboardProfile) => !profile.archived && profile.activeClient;

const getNationalSampleDropdownItems = (
    profile: PartnerDashboardProfile,
    navigateToProfileDetails: (profileId: string) => void,
    isNationalSampleAdmin: boolean,
    onDuplicate: (profileId: string) => void,
    onArchive: (profileId: string) => void,
    onRestore: (profileId: string) => void
) => {
    const primaryItems = [
        <DropdownItem
            key={`viewProfileDetailsItem-${profile.id}`}
            aria-label="viewNationalSampleProfileDetails"
            className="viewProfile"
            onClick={() => navigateToProfileDetails(profile.id)}
            itemText="View Profile Details"
            value="ViewProfile"
        />,
    ];

    const dropdownItems: JSX.Element[] = [];
    const primaryGroup = (
        <DropdownGroup key={`primaryNationalSampleActions-${profile.id}`} groupName="profileActionMenu">
            {primaryItems}
        </DropdownGroup>
    );

    if (isActiveNationalSample(profile)) {
        primaryItems.push(
            <DropdownItem
                key={`duplicateNationalSample-${profile.id}`}
                aria-label="duplicateNationalSample"
                onClick={() => onDuplicate(profile.id)}
                itemText="Duplicate to My Samples"
                value="DuplicateToMySamples"
            />
        );

        dropdownItems.push(primaryGroup);

        if (isNationalSampleAdmin) {
            dropdownItems.push(
                <DropdownGroup key={`archiveNationalSampleActions-${profile.id}`} groupName="profileActionMenu">
                    <DropdownItem
                        key={`archiveNationalSample-${profile.id}`}
                        aria-label="archiveNationalSample"
                        onClick={() => onArchive(profile.id)}
                        itemText="Archive National Sample"
                        value="ArchiveNationalSample"
                    />
                </DropdownGroup>
            );

            return dropdownItems;
        }

        return dropdownItems;
    }

    if (isNationalSampleAdmin) {
        primaryItems.push(
            <DropdownItem
                key={`restoreNationalSample-${profile.id}`}
                aria-label="restoreNationalSample"
                onClick={() => onRestore(profile.id)}
                itemText="Restore National Sample"
                value="RestoreNationalSample"
            />
        );
    }

    dropdownItems.push(primaryGroup);

    return dropdownItems;
};

const getColumns = (
    navigateToProfileDetails: (profileId: string) => void,
    sortColumnName: string,
    lastSortType: SortType | "",
    handleSorting: (columnName: string, sorting: "ascending" | "descending") => void,
    isDuplicatingNationalSample: boolean,
    isNationalSampleAdmin: boolean,
    onDuplicate: (profileId: string) => void,
    onArchive: (profileId: string) => void,
    onRestore: (profileId: string) => void
) => {
    const renderSortDropdown = (title: string, sortType: string, columnName: string) => (
        <div className="display-flex">
            <div>
                <HeaderIcon title={title} sortType={sortType} />
            </div>
            <div className="dropdown-align">
                <Dropdown
                    alignRight={true}
                    dropdownKind="menu"
                    iconNameOpen="menu_dots"
                    iconNameClose="menu_dots"
                    className="sort-dropdown"
                    buttonIcon="only"
                    buttonKind="borderless"
                    buttonSize="small"
                    rounded={false}
                    panelWidth={180}
                >
                    <DropdownItem
                        aria-label="Sort Ascending"
                        value="Sort Ascending"
                        onClick={() => handleSorting(columnName, "ascending")}
                    >
                        <div style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                            <div>Sort Ascending</div>
                            {sortType.includes("ascending") && (
                                <div>
                                    <i className="dds-icons icon--size-14">✔</i>
                                </div>
                            )}
                        </div>
                    </DropdownItem>
                    <DropdownItem
                        aria-label="Sort Descending"
                        value="Sort Descending"
                        onClick={() => handleSorting(columnName, "descending")}
                    >
                        <div style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                            <div>Sort Descending</div>
                            {sortType.includes("descending") && (
                                <div>
                                    <i className="dds-icons icon--size-14">✔</i>
                                </div>
                            )}
                        </div>
                    </DropdownItem>
                </Dropdown>
            </div>
        </div>
    );

    return [
    {
        Header: () => renderSortDropdown("Name", sortColumnName === "displayName" ? lastSortType : "", "displayName"),
        accessor: 'displayName',
        Cell: ({original}: {original: PartnerDashboardProfile}) => (
            <span data-testid="national-display-name" className="display-flex align-items-center">
                <Link
                    className="table-displayName"
                    to={`/Profile/${original.id}`}
                >
                    {original.displayName}
                </Link>
            </span>
        ),
        minWidth: 155,
    },
    {
        Header: () => renderSortDropdown("Last Modified", sortColumnName === "lastModifiedDate" ? lastSortType : "", "lastModifiedDate"),
        accessor: 'lastModifiedDate',
        Cell: ({original}: {original: PartnerDashboardProfile}) => (
            <span data-testid="national-last-modified">
                {toDisplayDateFormat(original.lastModifiedDate)}
            </span>
        ),
        width: 125,
    },
    {
        Header: () => renderSortDropdown("Modified By", sortColumnName === "lastModifiedByName" ? lastSortType : "", "lastModifiedByName"),
        accessor: 'lastModifiedByName',
        Cell: ({original}: {original: PartnerDashboardProfile}) => (
            <span data-testid="national-modified-by">
                {original.lastModifiedByName}
            </span>
        ),
        width: 116,
    },
    {
        Header: () => <></>,
        accessor: 'clientId',
        className: 'overflow-visible',
        Cell: ({original}: {original: PartnerDashboardProfile}) => (
            <ProfileProposalActionMenu
                disabled={isDuplicatingNationalSample}
                dropdownItems={getNationalSampleDropdownItems(original, navigateToProfileDetails, isNationalSampleAdmin, onDuplicate, onArchive, onRestore)}
            />
        ),
        minWidth: 32,
        maxWidth: 32,
    },
];
};

const NationalSamplesSection = ({
    profiles,
    isLoading,
    isDuplicatingNationalSample,
    filterOption,
    isNationalSampleAdmin,
    onDuplicateNationalSample,
    onArchiveNationalSample,
    onRestoreNationalSample,
    searchQuery, // Destructure the new prop
    isSearchActive, // Destructure the new prop
}: INationalSamplesSection) => {
    const history = useHistory();
    const [sortType, setSortType] = useState<SortType | "">("");
    const [lastSortType, setLastSortType] = useState<SortType | "">("");
    const [sortColumnName, setSortColumnName] = useState("");

    const filteredProfiles = useMemo(
        () => profiles.filter(NATIONAL_SAMPLE_FILTERS[filterOption]),
        [profiles, filterOption]
    );

    const hasNoNationalSamples = profiles.length === 0;

    const handleSorting = (columnName: string, sorting: "ascending" | "descending") => {
        setSortColumnName(columnName);

        const sortConfig = sortClientMapping[columnName];
        if (sortConfig) {
            const updatedSortType = sorting === "descending" ? sortConfig.descending : sortConfig.ascending;
            setSortType(updatedSortType);
            setLastSortType(updatedSortType);
        }
    };

    const sortedProfiles = useMemo(() => {
        if (!sortType) {
            return filteredProfiles;
        }

        return sortProfiles(sortType, [...filteredProfiles]);
    }, [filteredProfiles, sortType]);

    const showPagination = sortedProfiles.length > 0;
    const showNoNationalSamplesText = !isLoading && hasNoNationalSamples && !isSearchActive; // Show only when no search is active
    const columns = getColumns((profileId: string) => {
        history.push({
            pathname: `/Profile/${profileId}`,
        });
    }, sortColumnName, lastSortType, handleSorting, isDuplicatingNationalSample, isNationalSampleAdmin, onDuplicateNationalSample, onArchiveNationalSample, onRestoreNationalSample);

    return (
        <>
            <div className="table-header" aria-label="nationalSamplesLabel">
                <h4>
                    <i className="icons dds-icons icon--size-18 icon-padding">person_star</i>{/*
                    */}National Samples
                </h4>
            </div>
            <div className={showPagination ? "marginbottom-xxl national-table-spacing" : "marginbottom-3xxl national-table-spacing"}>
                <Table
                    id="NationalSamplesTable"
                    className="partner-dashboard-table"
                    caption="National Samples"
                    columns={columns}
                    data={sortedProfiles}
                    minRows={PARTNER_DASHBOARD_MIN_ROWS}
                    filterable={false}
                    noDataText={
                        isSearchActive && sortedProfiles.length === 0
                            ? NO_SEARCHED_NATIONAL_SAMPLES_TEXT
                            : getNoDataText(hasNoNationalSamples, isLoading, filterOption)
                    } // Updated to use isSearchActive
                    pageSizeDropUp={true}
                    pageSizeOptions={PARTNER_DASHBOARD_PAGE_SIZES}
                    rowHeight="medium"
                    showPagination={showPagination}
                    sortable={false}
                    defaultPageSize={NATIONAL_SAMPLES_DEFAULT_PAGE_SIZE}
                    SubComponent={null}
                />
                {showNoNationalSamplesText && (
                    <div className="national-samples-empty-state">
                        {NO_NATIONAL_SAMPLES_TEXT}
                    </div>
                )}
                {isDuplicatingNationalSample && (
                    <div className="national-samples-loading-overlay" aria-live="polite" aria-label="Duplicating national sample">
                        <LoadingIndicator className="national-samples-loading-content" />
                    </div>
                )}
            </div>
        </>
    );
};

export default NationalSamplesSection;
